/**
 * 邮箱+密码的三个端点（`/auth/email/verify-request`、`/auth/email/verify-confirm`、`/auth/login`）。
 *
 * 分层与 `emailLinkApi.ts` 一致：本地预检 → 传输 → 把服务端错误翻成人话。
 * 记得住密码就能在任何新设备直接登录，忘了就走 `emailLinkApi.ts` 的那封信。
 *
 * 三件事：
 *   1. 带鉴权发请求（`setPassword` 需要，`login` 不需要——新设备上本来就没有身份）；
 *   2. 把服务端的错误翻成人话；
 *   3. `login` 成功后调 `auth.adopt()`，把新的一对令牌落盘并切换当前身份。
 *
 * ★ 密码本身永不缓存、不落盘：拿到之后直接进请求体，组件 state 里也不留副本
 *   （提交完就该清空输入框，这是调用方的责任）。
 */
import type { PublicAccount } from '@poker/shared'

import { getAuthSession } from '../lobby/useAuth.js'
import { AuthError, parseJsonBody, readApiError, type AuthPairResponse, type AuthSession } from './session.js'

export type EmailAuthAction = 'bind' | 'login'

/** 失败的类别。界面按它决定文案与「现在能干什么」。 */
export type EmailAuthFailureKind =
  | 'network'
  | 'rate-limited'
  /** 登录：邮箱或密码不对（服务端刻意不区分两者，防止枚举已注册邮箱） */
  | 'credential-rejected'
  /** 绑定：这个邮箱已经绑在别的账号上 */
  | 'email-taken'
  /** 绑定：验证码不对/过期，或者改绑时旧密码不对 —— 服务端刻意不区分 */
  | 'verify-code-rejected'
  /** 这个客户端太旧，用的端点已经下线 */
  | 'client-outdated'
  /** 邮箱格式不对 / 密码太短，本地校验本该拦住的输入 */
  | 'invalid'
  | 'not-authenticated'
  | 'server'

export class EmailAuthError extends Error {
  constructor(
    override readonly message: string,
    readonly kind: EmailAuthFailureKind,
    readonly retryAfterMs: number | null = null,
  ) {
    super(message)
    this.name = 'EmailAuthError'
  }
}

export interface EmailAuthApiOptions {
  readonly auth?: AuthSession
  readonly fetchImpl?: typeof fetch
}

// ============================================================================
// 本地校验：不合规的输入本地拦住，不占服务端的限流额度
// ============================================================================

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const PASSWORD_MIN_CHARS = 8

/** 返回具体问题；null = 可以提交。与服务端 `EmailSchema`/`PasswordSchema` 同口径。 */
export function emailProblem(raw: string): string | null {
  const s = raw.trim()
  if (s === '') return '请输入邮箱'
  if (!EMAIL_SHAPE.test(s)) return '邮箱格式不对'
  return null
}

export function passwordProblem(raw: string): string | null {
  if (raw.length < PASSWORD_MIN_CHARS) return `密码至少 ${String(PASSWORD_MIN_CHARS)} 位`
  return null
}

// ============================================================================
// 错误 → 人话
// ============================================================================

function waitHint(retryAfterMs: number | null): string {
  if (retryAfterMs === null || retryAfterMs <= 0) return '等一会儿再试'
  const secs = Math.ceil(retryAfterMs / 1000)
  if (secs <= 90) return `等 ${String(secs)} 秒再试`
  const mins = Math.ceil(secs / 60)
  return mins <= 90 ? `等 ${String(mins)} 分钟再试` : '等一会儿再试'
}

/** 每一句都要回答「出了什么事 + 现在能干什么」，绝不把服务端 message 抬上屏。 */
export function describeEmailAuthFailure(
  kind: EmailAuthFailureKind,
  retryAfterMs: number | null = null,
): string {
  switch (kind) {
    case 'network':
      return '网络没连上，检查一下再试'
    case 'rate-limited':
      return `试过太多次了，${waitHint(retryAfterMs)}`
    case 'credential-rejected':
      // 服务端对「邮箱不存在」和「密码错」返回完全一样的 401——区分开就是送人一台枚举机
      return '邮箱或密码不对'
    case 'email-taken':
      return '这个邮箱已经绑定了另一个账号'
    case 'verify-code-rejected':
      // ★ 必须写成「再输一次」而不是「刷新页面」：用户收件箱里那枚码可能还有效，
      //   一刷新面板就卸载，他就再也没有地方填它了
      return '验证码不对或者过期了，检查一下再输，或者重新发一封'
    case 'client-outdated':
      return 'App 版本太旧了，完全退出后重新打开'
    case 'invalid':
      return '邮箱或密码格式不对'
    case 'not-authenticated':
      return '本机的登录状态过期了，刷新一下页面再来'
    default:
      return '服务器出了点问题，再试一次'
  }
}

function classify(err: AuthError, action: EmailAuthAction): EmailAuthFailureKind {
  if (err.code === 'E_NETWORK' || err.status === 0) return 'network'
  // ★ 先看 code 再看 status：验证码失败也是 401，但它与「登录态过期」是两回事，
  //   翻错一句就把「再输一次」变成「刷新页面 → 面板卸载 → 手里那枚码没地方填」
  if (err.code === 'E_VERIFY_CODE_INVALID') return 'verify-code-rejected'
  if (err.code === 'E_CLIENT_OUTDATED' || err.status === 410) return 'client-outdated'
  if (err.status === 429 || err.code === 'E_RATE_LIMITED') return 'rate-limited'
  if (err.status === 409 || err.code === 'E_EMAIL_TAKEN') return 'email-taken'
  if (err.status === 401) return action === 'login' ? 'credential-rejected' : 'not-authenticated'
  if (err.status === 400) return 'invalid'
  return 'server'
}

function toEmailAuthError(e: unknown, action: EmailAuthAction): EmailAuthError {
  if (e instanceof EmailAuthError) return e
  const err = e instanceof AuthError ? e : new AuthError(String(e), 'E_NETWORK', 0, false)
  const kind = classify(err, action)
  return new EmailAuthError(describeEmailAuthFailure(kind, err.retryAfterMs), kind, err.retryAfterMs)
}

// ============================================================================
// 传输
// ============================================================================

interface RequestInput {
  readonly auth: AuthSession
  readonly fetchImpl: typeof fetch
  readonly path: string
  readonly body: unknown
  readonly authed: boolean
}

async function request(input: RequestInput): Promise<unknown> {
  const headers: Record<string, string> = { 'content-type': 'application/json' }
  if (input.authed) {
    // ★ 走同一个单飞刷新：这里自己发一次 /auth/refresh 会和 WS 的刷新撞上轮换检测
    headers['authorization'] = `Bearer ${await input.auth.ensureAccessToken()}`
  }
  let res: Response
  try {
    res = await input.fetchImpl(`${input.auth.baseUrl()}/api${input.path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(input.body),
    })
  } catch (e) {
    throw new AuthError(`网络不可用：${String(e)}`, 'E_NETWORK', 0, false)
  }
  const parsed = parseJsonBody(await res.text())
  if (!res.ok) throw readApiError(res.status, parsed)
  return parsed
}

function resolve(options: EmailAuthApiOptions): { auth: AuthSession; fetchImpl: typeof fetch } {
  return {
    auth: options.auth ?? getAuthSession(),
    fetchImpl: options.fetchImpl ?? ((...args) => globalThis.fetch(...args)),
  }
}

// ============================================================================
// 两个端点
// ============================================================================

/**
 * 绑定邮箱 · 第一步：把 6 位验证码发到那个邮箱。
 *
 * ★ 为什么是验证码不是验证链接：点链接会把用户扔进另一个浏览器上下文
 *   （微信 WebView 里的邮件 → 系统浏览器），他填了一半的表单就回不去了。
 *   6 位数字是让他切到邮箱 app 看一眼、切回来手输，上下文不换。
 *
 * ★ 服务端对「这个邮箱已经被别人绑了」**不分叉** —— 一律签、一律发、一律同样的 200。
 *   所以这里也不该制造出任何差别：占用与否要到第二步才会知道。
 */
export async function requestVerifyCode(email: string, options: EmailAuthApiOptions = {}): Promise<void> {
  const { auth, fetchImpl } = resolve(options)
  const problem = emailProblem(email)
  if (problem !== null) throw new EmailAuthError(problem, 'invalid')
  try {
    await request({
      auth,
      fetchImpl,
      path: '/auth/email/verify-request',
      body: { email: email.trim() },
      authed: true,
    })
  } catch (e) {
    throw toEmailAuthError(e, 'bind')
  }
}

/**
 * 绑定邮箱 · 第二步：验码 + 设密码，服务端在同一个事务里完成，没有中间态。
 *
 * @param currentPassword 改绑（这个账号已经绑过邮箱）时必填。
 *   不验旧密码的话，拿到一枚被盗 access token 的人把账号绑到自己邮箱，
 *   原主人的旧邮箱当场失效，而他没有任何找回路径。
 */
export async function confirmVerifyCode(
  email: string,
  code: string,
  password: string,
  currentPassword?: string,
  options: EmailAuthApiOptions = {},
): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  const eProblem = emailProblem(email)
  const pProblem = passwordProblem(password)
  if (eProblem !== null || pProblem !== null) {
    throw new EmailAuthError(eProblem ?? pProblem ?? '', 'invalid')
  }
  if (!/^[0-9]{6}$/.test(code)) throw new EmailAuthError('验证码是 6 位数字', 'invalid')
  try {
    const body = (await request({
      auth,
      fetchImpl,
      path: '/auth/email/verify-confirm',
      body: {
        email: email.trim(),
        code,
        password,
        ...(currentPassword === undefined || currentPassword === '' ? {} : { currentPassword }),
      },
      authed: true,
    })) as { account?: PublicAccount } | null
    if (body?.account == null) throw new EmailAuthError(describeEmailAuthFailure('server'), 'server')
    return body.account
  } catch (e) {
    throw toEmailAuthError(e, 'bind')
  }
}

/**
 * 邮箱登录：在任意新设备上，不需要先碰旧设备。
 *
 * ★ 成功后 `auth.adopt(pair)`——新的一对令牌落盘并切换当前身份。
 *   调用方还要重建 WS（那条连接仍然认着旧账号），见 `app/session.ts` 的
 *   `restartOnlineSession`（与邮件链接兑换的收尾方式完全一致）。
 */
export async function loginWithEmail(
  email: string,
  password: string,
  options: EmailAuthApiOptions = {},
): Promise<PublicAccount> {
  const { auth, fetchImpl } = resolve(options)
  const eProblem = emailProblem(email)
  if (eProblem !== null || password === '') {
    throw new EmailAuthError(eProblem ?? '请输入密码', 'invalid')
  }
  try {
    const body = (await request({
      auth,
      fetchImpl,
      path: '/auth/login',
      body: { email: email.trim(), password, deviceId: auth.deviceId() },
      authed: false,
    })) as Partial<AuthPairResponse> | null
    if (
      body == null ||
      typeof body.accessToken !== 'string' ||
      typeof body.refreshToken !== 'string' ||
      body.account == null
    ) {
      throw new EmailAuthError(describeEmailAuthFailure('server'), 'server')
    }
    return auth.adopt(body as AuthPairResponse)
  } catch (e) {
    throw toEmailAuthError(e, 'login')
  }
}
